import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../utils/api";
import LiveStrip from "../../components/LiveStrip";
import "../../styles/parkingVsTaxi.css";
import HeathrowDepartureRecommendationSummary from "./flows/HeathrowDepartureRecommendationSummary";
import HeathrowReturnRecommendationSummary from "./flows/HeathrowReturnRecommendationSummary";
import SecureJourneyAccessStrip from "./components/SecureJourneyAccessStrip";

const LHR_API = {
  flightSuggest: "/api/heathrow/flights/suggest",
  flightLookup: "/api/heathrow/flights/lookup",
  flightSchedules: "/api/heathrow/flights/schedules",
  outwardPlan: "/api/lhr/v1/flight-plan",
  returnPlan: "/api/lhr/v1/return/flight-plan",
  journeyByToken: (token) => `/api/lhr/v1/journey/${encodeURIComponent(token)}`,
  journeySendEmail: (token) => `/api/lhr/v1/journey/${encodeURIComponent(token)}/email`,
  journeyVerifyCode: (token) => `/api/lhr/v1/journey/${encodeURIComponent(token)}/verify-code`,
};

const TRAVEL_DIRECTIONS = {
  TOWARDS: "to_airport",
  RETURN: "from_airport",
};

const useCompactMobile = (maxWidth = 768) => {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsCompact(!!e.matches);
    setIsCompact(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidth]);

  return isCompact;
};

const normalizeReturnPlanToView = (data, form) => {
  const taxiMode = data?.modes?.taxi || {};
  const driveMode = data?.modes?.self_drive || {};
  const railMode = data?.modes?.rail || {};
  const googleTransit = railMode?.google_transit || null;
  const railSegments = Array.isArray(railMode?.segments) ? railMode.segments : [];
  const gatewaySeg = railSegments.find((s) => s.id === "terminal_to_gateway") || null;
  const railSeg = railSegments.find((s) => s.id === "rail") || null;
  const lastMileSeg = railSegments.find((s) => s.id === "last_mile") || null;
  const railJourney = railMode?.best_candidate?.journey || null;
  const railFareRaw = railMode?.best_candidate?.fare || null;
  const railFarePence = railFareRaw?.total_pence;
  const railFareGbp =
    Number.isFinite(Number(railFareRaw?.total_gbp))
      ? Number(railFareRaw.total_gbp)
      : Number.isFinite(Number(railFarePence))
      ? Number((Number(railFarePence) / 100).toFixed(2))
      : null;
  const gatewayCrs = gatewaySeg?.to || railSeg?.from || "HXX";
  const destCrs = railSeg?.to || "Home station";
  const homePostcode = form?.pickup_postcode || "Home";
  const terminalExitAt = data?.return_guidance?.terminal_exit_at || null;

  const normalizedRailJourney = railJourney
    ? {
        ...railJourney,
        target_delta_mins: 0,
        fare: railFareGbp != null ? { total_gbp: railFareGbp } : null,
      }
    : null;

  return {
    flight: data?.flight || null,
    recommendation: data?.recommendation || null,
    return_guidance: data?.return_guidance || null,
    input_prefill: {
      pickup_postcode: homePostcode,
      flight_number: form?.flight_number || "",
      flight_date: form?.flight_date || "",
      flight_direction: "Arrival",
      flight_anchor_utc: data?.flight?.arrival || data?.flight?.scheduled_arrival || form?.flight_anchor_utc || "",
      travel_direction: TRAVEL_DIRECTIONS.RETURN,
      bags: Number(form?.bags || 0),
      parking_duration_days: Number(form?.parking_duration_days || 4),
    },
    rail: {
      request: {
        terminal_exit_at: terminalExitAt,
        terminal_arrive_by: terminalExitAt,
        station_depart_after: terminalExitAt,
        gateway_crs: gatewayCrs,
        gateway_depart_after: data?.modes?.rail?.request?.gateway_depart_after || null,
        selected_gateway_depart_at: data?.modes?.rail?.request?.selected_gateway_depart_at || null,
        resolved_dest_crs: gatewayCrs,
        dest_crs: gatewayCrs,
      },
      segments: [
        {
          id: "first_mile",
          from: "LHR",
          to: gatewayCrs,
          mode: "road",
          eta_safe_mins: gatewaySeg?.eta_safe_mins ?? null,
          status: railMode?.overall_disruption?.level || "unknown",
          headline: gatewaySeg?.headline || "Terminal to gateway",
        },
        {
          id: "rail",
          from: gatewayCrs,
          to: destCrs,
          mode: "rail",
          status: railMode?.overall_disruption?.level || "unknown",
          journeys: normalizedRailJourney ? [normalizedRailJourney] : [],
          checked_sources: ["nr_journey", "nr_incidents"],
          last_checked: new Date().toISOString(),
        },
        {
          id: "airport_hop",
          from: destCrs,
          to: homePostcode,
          mode: lastMileSeg?.mode || "walk",
          eta_safe_mins: lastMileSeg?.walk_mins ?? null,
          status: "unknown",
          headline: "Station to home",
        },
      ],
      best_candidate: {
        selected_station: gatewayCrs,
        meets_target: true,
        target_best_delta_mins: 0,
        target_band: "green",
        fare: railFareGbp != null ? { total_gbp: railFareGbp } : null,
        journeys: normalizedRailJourney ? [normalizedRailJourney] : [],
      },
      overall_disruption: railMode?.overall_disruption || { level: "unknown", reasons: ["no_data"] },
      weather: railMode?.weather || null,
      no_rail_reason: normalizedRailJourney ? null : (railMode?.no_rail_reason || "No rail options found for return journey."),
      google_transit: googleTransit,
      alternate_transit_available: !!railMode?.alternate_transit_available,
    },
    taxi: {
      road: taxiMode?.road || null,
      fare: {
        price: taxiMode?.cost_gbp ?? null,
        source: taxiMode?.cost_source || null,
        distance_km: taxiMode?.road?.distance_km ?? null,
        vehicle: null,
      },
      weather: taxiMode?.weather || null,
    },
    drive: {
      road: driveMode?.road || null,
      fuel: {
        fuel: { fuelCost: driveMode?.fuel_cost_gbp ?? null },
        distance_km: driveMode?.road?.distance_km ?? null,
      },
      parking: { all: [{ product_name: "LHR exit", transfer_minutes: 0, price_gbp: 0 }] },
      parking_warning: null,
      weather: driveMode?.weather || null,
    },
    details: null,
    diagnostics: data?.diagnostics || null,
  };
};

const applyUiProjectionPreferred = (baseResult, projection) => {
  if (!projection || typeof projection !== "object") return baseResult;
  const base = baseResult && typeof baseResult === "object" ? JSON.parse(JSON.stringify(baseResult)) : {};
  const finiteOrNull = (v) => {
    if (v == null) return null;
    if (typeof v === "string" && v.trim() === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const hero = projection?.hero || {};
  const cards = projection?.cards || {};
  const timelines = projection?.timelines || {};
  const sourceLevels = projection?.source_levels || {};
  const flow = String(projection?.flow || "").toLowerCase();

  if (!base.recommendation || typeof base.recommendation !== "object") base.recommendation = {};
  if (hero?.recommendation_summary) base.recommendation.summary = hero.recommendation_summary;
  if (projection?.recommended_mode) base.recommendation.recommended_mode = projection.recommended_mode;
  if (projection?.overall_level) {
    base.recommendation.overall_disruption = {
      ...(base.recommendation.overall_disruption || {}),
      level: String(projection.overall_level || "").toLowerCase(),
    };
  }

  if (!base.input_prefill || typeof base.input_prefill !== "object") base.input_prefill = {};
  if (projection?.flight_number && !base.input_prefill.flight_number) base.input_prefill.flight_number = projection.flight_number;
  if (projection?.flight_date && !base.input_prefill.flight_date) base.input_prefill.flight_date = projection.flight_date;
  if (projection?.direction && !base.input_prefill.flight_direction) base.input_prefill.flight_direction = projection.direction;
  if (projection?.anchor_utc && !base.input_prefill.flight_anchor_utc) base.input_prefill.flight_anchor_utc = projection.anchor_utc;
  if (projection?.postcode && !base.input_prefill.pickup_postcode) base.input_prefill.pickup_postcode = projection.postcode;
  if (!base.input_prefill.travel_direction) {
    base.input_prefill.travel_direction = flow === "return" ? TRAVEL_DIRECTIONS.RETURN : TRAVEL_DIRECTIONS.TOWARDS;
  }

  if (!base.flight || typeof base.flight !== "object") base.flight = {};
  if (hero?.flight_status) base.flight.status = hero.flight_status;
  if (projection?.anchor_status) base.flight.status = projection.anchor_status;
  if (projection?.anchor_utc) {
    if (String(projection?.direction || "").toLowerCase().startsWith("arr")) {
      if (!base.flight.scheduled_arrival_utc) base.flight.scheduled_arrival_utc = projection.anchor_utc;
    } else {
      if (!base.flight.scheduled_departure_utc) base.flight.scheduled_departure_utc = projection.anchor_utc;
    }
  }
  if (projection?.flight_number && !base.flight.number) base.flight.number = projection.flight_number;

  if (!base.modes || typeof base.modes !== "object") base.modes = {};
  if (!base.details || typeof base.details !== "object") base.details = {};
  const modeKeys = ["train", "self_drive", "taxi"];
  modeKeys.forEach((m) => {
    const card = cards?.[m] || {};
    const tl = timelines?.[m] || {};
    if (!base.modes[m] || typeof base.modes[m] !== "object") base.modes[m] = {};
    if (card?.summary) base.modes[m].summary = card.summary;
    if (card?.leave_home_at) base.modes[m].leave_by = card.leave_home_at;
    if (finiteOrNull(card?.cost_gbp) != null) base.modes[m].cost_gbp = Number(card.cost_gbp);
    if (finiteOrNull(card?.duration_mins) != null) base.modes[m].duration_mins = Number(card.duration_mins);
    if (card?.status) {
      base.modes[m].overall_disruption = {
        ...(base.modes[m].overall_disruption || {}),
        level: String(card.status || "").toLowerCase(),
      };
    }

    if (!base.details[m] || typeof base.details[m] !== "object") base.details[m] = {};
    if (Array.isArray(tl?.steps) && tl.steps.length) {
      base.details[m].steps = tl.steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      }));
    }
    if (Array.isArray(tl?.terminal_common_steps)) {
      base.details[m].terminal_common_steps = tl.terminal_common_steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      }));
    }
    if (finiteOrNull(tl?.terminal_margin_mins) != null) {
      base.details[m].margin_to_terminal_target_mins = Number(tl.terminal_margin_mins);
    }
    if (tl?.checked_at) {
      base.details[m].checked_at = tl.checked_at;
    }
  });

  if (!base.rail || typeof base.rail !== "object") base.rail = {};
  if (!base.taxi || typeof base.taxi !== "object") base.taxi = {};
  if (!base.drive || typeof base.drive !== "object") base.drive = {};
  if (sourceLevels?.rail) {
    base.rail.overall_disruption = {
      ...(base.rail.overall_disruption || {}),
      level: String(sourceLevels.rail || "").toLowerCase(),
    };
  }
  if (sourceLevels?.road) {
    if (!base.taxi.road || typeof base.taxi.road !== "object") base.taxi.road = {};
    if (!base.drive.road || typeof base.drive.road !== "object") base.drive.road = {};
    base.taxi.road.status = String(sourceLevels.road || "").toLowerCase();
    base.drive.road.status = String(sourceLevels.road || "").toLowerCase();
  }
  if (sourceLevels?.taxi_road) {
    if (!base.taxi.road || typeof base.taxi.road !== "object") base.taxi.road = {};
    base.taxi.road.status = String(sourceLevels.taxi_road || "").toLowerCase();
  }
  if (sourceLevels?.self_drive_road) {
    if (!base.drive.road || typeof base.drive.road !== "object") base.drive.road = {};
    base.drive.road.status = String(sourceLevels.self_drive_road || "").toLowerCase();
  }
  if (sourceLevels?.weather) {
    if (!base.rail.weather || typeof base.rail.weather !== "object") base.rail.weather = {};
    base.rail.weather.risk = {
      ...(base.rail.weather.risk || {}),
      level: String(sourceLevels.weather || "").toLowerCase(),
    };
  }

  if (projection?.source_levels && typeof projection.source_levels === "object") {
    base.source_levels = { ...(base.source_levels || {}), ...projection.source_levels };
  }
  if (projection?.monitor_targets && typeof projection.monitor_targets === "object") {
    base.monitor_targets = projection.monitor_targets;
  }
  if (projection?.policy_state && typeof projection.policy_state === "object") {
    base.policy_state = projection.policy_state;
  }
  if (projection?.source_monitor_execution_id != null) {
    base.source_monitor_execution_id = projection.source_monitor_execution_id;
  }
  if (projection?.source_monitor_plan_version != null) {
    base.source_monitor_plan_version = projection.source_monitor_plan_version;
  }
  if (Array.isArray(hero?.terminal_common_steps)) {
    if (!base.details || typeof base.details !== "object") base.details = {};
    base.details.terminal_common = {
      ...(base.details.terminal_common || {}),
      steps: hero.terminal_common_steps.map((s) => ({
        id: s?.id || null,
        at: s?.at || null,
        title: s?.title || null,
        from: s?.from || null,
        to: s?.to || null,
        mode: s?.mode || null,
        duration_mins: finiteOrNull(s?.duration_mins),
        status: s?.status || null,
        status_reason: s?.status_reason || null,
        notes: Array.isArray(s?.notes) ? s.notes : [],
        confidence: s?.confidence || null,
        source: s?.source || null,
        disclaimer: s?.disclaimer || null,
      })),
    };
  }
  base.ui_projection = projection;

  return base;
};

const SEO_COLLAPSIBLE_SECTIONS = [
  {
    heading: "How this live journey planner calculates your route",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          This live journey planner is designed to help passengers travel to Heathrow with real-time awareness of road, rail and flight conditions.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          When you enter your postcode and flight details, the system builds a door-to-terminal baseline for each available travel mode: train, taxi and self-drive. We calculate expected journey duration from your home to the airport terminal, including first-mile and last-mile transfers.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          For rail journeys, we use official National Rail data feeds to generate route options via Heathrow rail gateways such as Heathrow Express, Elizabeth line and connected National Rail interchanges. Each rail leg is mapped and baselined so we can assess transfer times and interchange risk.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          For road journeys, we use live Google traffic data combined with National Highways incident feeds to evaluate motorway and major A-road conditions on the Heathrow corridor. This allows us to establish a realistic travel duration under current conditions.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          Parking costs are based on Heathrow parking reference data, while taxi comparisons use live fare calculations where available. All modes are then assessed against your flight departure time to determine when you should leave home.
        </p>
        <p style={{ margin: 0 }}>
          The result is not just a route suggestion; it is a time-aware travel recommendation built around your specific flight.
        </p>
      </>
    ),
  },
  {
    heading: "What we monitor before departure",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          After your journey is created, the system continues monitoring your route in the lead-up to departure.
        </p>
        <p style={{ margin: "0 0 10px" }}>We track:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Official National Rail disruption updates across each CRS station involved in your route.</li>
          <li>RailData service alerts affecting specific lines and interchanges.</li>
          <li>Transport for London (TfL) disruption feeds where London interchanges are part of your journey.</li>
          <li>National Highways incident feeds for motorway and trunk road segments on your corridor.</li>
          <li>Live Google traffic conditions to detect congestion or unexpected delays.</li>
          <li>Weather forecasts at both your home postcode and Heathrow.</li>
          <li>Flight status updates including departure time shifts and gate information, where available.</li>
        </ul>
        <p style={{ margin: 0 }}>
          Each data source is evaluated against your original journey baseline. If a change affects your arrival margin at the terminal, the system recalculates and updates your recommended leave time.
        </p>
      </>
    ),
  },
  {
    heading: "Hyper monitoring in the final hours before departure",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          In the final 8 hours before your planned terminal arrival, monitoring automatically enters a higher-frequency hyper mode.
        </p>
        <p style={{ margin: "0 0 10px" }}>During this window, the system increases checks across:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Road traffic conditions.</li>
          <li>Rail services and gateway transfers.</li>
          <li>Airport weather conditions.</li>
          <li>Flight timing updates.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          This allows us to detect late-developing congestion, rail cancellations or corridor disruptions that may not have been visible earlier.
        </p>
        <p style={{ margin: 0 }}>
          If significant changes occur, you may receive alerts advising you to leave earlier or consider an alternative transfer mode. Hyper monitoring is designed to reduce last-minute uncertainty and help you arrive at Heathrow with confidence.
        </p>
      </>
    ),
  },
  {
    heading: "Independent, data-driven travel monitoring",
    body: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          This journey monitoring system operates independently using a combination of official transport data feeds, live traffic services and real-time weather forecasting.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          It is designed to provide passengers with a structured, data-driven view of their route to Heathrow, rather than relying solely on static journey times.
        </p>
        <p style={{ margin: "0 0 10px" }}>By combining:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Official rail data.</li>
          <li>National Highways incident information.</li>
          <li>Live Google traffic.</li>
          <li>Flight status updates.</li>
          <li>Airport parking and transfer modelling.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          We create a continuously evaluated journey profile tailored to your specific departure.
        </p>
        <p style={{ margin: 0 }}>
          The aim is simple: help you leave at the right time, using the most reliable transfer mode available.
        </p>
      </>
    ),
  },
];

const SEO_FAQ_SECTIONS = [
  {
    question: "How early should I arrive at Heathrow?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          Heathrow timing depends on your airline, terminal and baggage/check-in requirements, but most passengers should still plan around the airline and airport guidance for arriving well before departure.
        </p>
        <p style={{ margin: "0 0 10px" }}>However, the correct time to leave home depends on:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Road congestion levels.</li>
          <li>Rail interchange risk.</li>
          <li>Weather conditions.</li>
          <li>Parking transfer time.</li>
          <li>Flight departure changes.</li>
        </ul>
        <p style={{ margin: 0 }}>
          This journey planner calculates a door-to-terminal arrival time, not just a driving duration, so that you reach the airport with an appropriate buffer based on your specific route.
        </p>
      </>
    ),
  },
  {
    question: "What is the best rail station for Heathrow?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>Heathrow has direct rail access, but the best gateway depends on your route. Common options include:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Heathrow Terminals 2 & 3.</li>
          <li>Heathrow Terminal 4.</li>
          <li>Heathrow Terminal 5.</li>
          <li>Paddington or central London interchanges for through rail journeys.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>The planner evaluates the full route, including terminal walk times and connected rail legs, before recommending a rail option.</p>
        <p style={{ margin: 0 }}>
          The most reliable gateway depends on your origin postcode, service frequency and interchange timing. The system evaluates each viable gateway and baselines the full rail-to-terminal journey before making a recommendation.
        </p>
      </>
    ),
  },
  {
    question: "How reliable is driving to Heathrow?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          Driving reliability depends largely on conditions along the main Heathrow corridors, including motorway and major A-road approaches into the airport.
        </p>
        <p style={{ margin: "0 0 10px" }}>
          Traffic incidents, motorway lane closures and peak-hour congestion can significantly affect journey time.
        </p>
        <p style={{ margin: "0 0 10px" }}>This monitoring system uses:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>National Highways incident feeds.</li>
          <li>Live Google traffic data.</li>
          <li>Weather forecasts.</li>
        </ul>
        <p style={{ margin: 0 }}>
          This helps assess corridor-level risk and detect deviations from baseline travel times.
        </p>
      </>
    ),
  },
  {
    question: "What happens if there is a rail disruption before my flight?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>If your journey includes rail travel, we monitor:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Official National Rail disruption feeds.</li>
          <li>RailData alerts at each CRS station.</li>
          <li>Interchange risk at gateway stations.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          If a cancellation, delay or service change affects your planned arrival window, the system reassesses your margin and may recommend an earlier departure time or an alternative transfer mode.
        </p>
        <p style={{ margin: 0 }}>
          In higher-risk windows, notifications are sent automatically to verified users.
        </p>
      </>
    ),
  },
  {
    question: "What is Hyper Mode before departure?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          Hyper Mode activates automatically in the final hours before your planned airport arrival.
        </p>
        <p style={{ margin: "0 0 10px" }}>During this period, monitoring frequency increases and the system checks:</p>
        <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
          <li>Road congestion changes.</li>
          <li>Rail service updates.</li>
          <li>Weather shifts.</li>
          <li>Flight timing adjustments.</li>
        </ul>
        <p style={{ margin: "0 0 10px" }}>
          This allows the journey recommendation to adapt to late-developing disruptions.
        </p>
        <p style={{ margin: 0 }}>
          If significant changes are detected, alerts are issued so you can adjust in time.
        </p>
      </>
    ),
  },
  {
    question: "Is this an official Heathrow system?",
    answer: (
      <>
        <p style={{ margin: "0 0 10px" }}>
          No. This is an independent journey monitoring system that uses official transport data feeds and live traffic services to help passengers plan travel to Heathrow.
        </p>
        <p style={{ margin: 0 }}>
          It is designed to provide a structured, data-driven view of route conditions, but it does not replace airline or airport guidance.
        </p>
      </>
    ),
  },
];

function CollapsibleSeoSections() {
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: "#64748b", marginBottom: 8 }}>
        HOW IT WORKS
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {SEO_COLLAPSIBLE_SECTIONS.map((section) => (
          <details key={section.heading} className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ {section.heading}</summary>
            <div className="train-collapsible-body" style={{ color: "#475467", fontSize: 14, lineHeight: 1.65 }}>
              {section.body}
            </div>
          </details>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
        Expand each section for methodology, monitoring scope and final-hours operating mode.
      </div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: "#64748b", marginTop: 16, marginBottom: 8 }}>
        FAQS
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {SEO_FAQ_SECTIONS.map((faq) => (
          <details key={faq.question} className="train-collapsible">
            <summary className="train-summary-bar">❓ {faq.question}</summary>
            <div className="train-collapsible-body" style={{ color: "#475467", fontSize: 14, lineHeight: 1.65 }}>
              {faq.answer}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function HeathrowRelatedToolsSection() {
  return (
    <section
      style={{
        marginTop: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fff",
      }}
    >
      <strong style={{ color: "#0f172a" }}>Plan your Heathrow journey:</strong>
      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
        This is the main live planner. Use the related tools below when you need a narrower Heathrow-specific view such as leave-time guidance, route comparisons or return-home planning.
      </div>
      <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0, paddingLeft: 18, color: "#1f2937" }}>
        <li>
          <Link to="/heathrow/when-to-leave-for-heathrow">When to leave for Heathrow</Link>
          : a cleaner leave-time-focused view if you want timing guidance without the full live planner.
        </li>
        <li>
          <Link to="/heathrow/london-to-heathrow">London to Heathrow</Link>
          : London-specific road, rail and taxi guidance into Heathrow.
        </li>
        <li>
          <Link to="/heathrow/return-journey-monitoring">Return journey monitoring from Heathrow</Link>
          : plan the trip home after landing using the same live timing logic.
        </li>
        <li>
          <Link to="/how-to-get-to-heathrow">How to get to Heathrow</Link>
          : the broader airport access guide covering station choices, transfers and practical routing.
        </li>
        <li>
          <Link to="/heathrow/train-vs-taxi">Train vs taxi to Heathrow</Link>
          : compare the main public transport and road options side by side.
        </li>
        <li>
          <Link to="/international-students/uk-arrival-guide">Student arrival assistant</Link>
          : UK airport pickup support for student arrivals.
        </li>
      </ul>
    </section>
  );
}

export default function HowToGetToLHRv2() {
  const isPlannerMode = process.env.REACT_APP_SITE_MODE === "planner";
  const canonicalUrl = isPlannerMode
    ? "https://www.airportjourneyplanner.com/heathrow"
    : "https://www.cheapcabgrab.com/heathrow/live-journey-planner";
  const USE_MOCK = false;
  const MOCK_RESPONSE = null; // drop a stored JSON here if you want to mock

  const DEFAULT_PICKUP = "W1A 1AA";
  const DEFAULT_FLIGHT_NUMBER_TO_LHR = "BA438";
  const DEFAULT_FLIGHT_NUMBER_FROM_LHR = "EI184";

  const [form, setForm] = useState({
    pickup_postcode: DEFAULT_PICKUP,
    flight_number: DEFAULT_FLIGHT_NUMBER_TO_LHR,
    flight_date: "",
    flight_direction: "",
    flight_anchor_utc: "",
    passengers: 2,
    bags: 2,
    preferred_modes: ["taxi", "rail", "self_drive"],
    parking_duration_days: 4,
    buffer_mode: "standard",
  });

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [pickupActiveIdx, setPickupActiveIdx] = useState(-1);
  const [pickupWarning, setPickupWarning] = useState("");
  const [postcodeError, setPostcodeError] = useState("");
  const [postcodeValidating, setPostcodeValidating] = useState(false);
  const [pickupFocused, setPickupFocused] = useState(false);
  const pickupTimer = useRef(null);

  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightActiveIdx, setFlightActiveIdx] = useState(-1);
  const [flightFocused, setFlightFocused] = useState(false);
  const flightTimer = useRef(null);
  const [flightError, setFlightError] = useState("");

  const [flightSchedules, setFlightSchedules] = useState({ Arrival: [], Departure: [] });
  const [flightDates, setFlightDates] = useState([]);
  const [flightDateError, setFlightDateError] = useState("");
  const [flightDirection, setFlightDirection] = useState("Departure");
  const [travelDirection, setTravelDirection] = useState(TRAVEL_DIRECTIONS.TOWARDS);
  const flightScheduleTimer = useRef(null);
  const flightScheduleRequestSeq = useRef(0);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [subErrors, setSubErrors] = useState([]);
  const [journeyMeta, setJourneyMeta] = useState({
    journey_id: null,
    token: "",
    token_expires_at: null,
    email: "",
    email_verified: false,
    creating: false,
    emailing: false,
    verifying: false,
    email_sent: false,
    note: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const resultContainerRef = useRef(null);
  const loadingTimers = useRef([]);
  const [scrollToResultsPending, setScrollToResultsPending] = useState(false);
  const isCompactMobile = useCompactMobile(768);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryToken = String(searchParams.get("token") || "").trim();
  const stepParam = String(searchParams.get("step") || "").trim().toLowerCase();
  const showMobileResultsScreen = isCompactMobile && stepParam === "results" && !!result;
  const [isTokenBoundJourney, setIsTokenBoundJourney] = useState(false);
  const pickupListboxId = "ema-v2-pickup-suggestions";
  const pickupWarningId = "ema-v2-pickup-warning";
  const postcodeErrorId = "ema-v2-postcode-error";
  const flightListboxId = "ema-v2-flight-suggestions";
  const flightErrorId = "ema-v2-flight-error";
  const flightDateErrorId = "ema-v2-flight-date-error";
  const formErrorId = "ema-v2-form-error";

  const flightNumKey = (n) => String(n || "").replace(/\s+/g, "").toUpperCase();
  const formatScheduleLabel = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/London",
    });
  };
  const scheduleAnchorTime = (s) =>
    s?.anchor_time_utc || s?.estimated_time_utc || s?.scheduled_anchor_utc || s?.scheduled_time_utc || null;
  const scheduleDateKey = (s) => {
    const anchor = scheduleAnchorTime(s);
    if (!anchor) {
      const dated = String(s?.date || s?.snapshot_date || "").trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(dated) ? dated : String(dated).slice(0, 10) || "";
    }
    const d = new Date(anchor);
    if (Number.isNaN(d.getTime())) {
      const dated = String(s?.date || s?.snapshot_date || "").trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(dated) ? dated : String(dated).slice(0, 10) || "";
    }
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    return year && month && day ? `${year}-${month}-${day}` : String(anchor).slice(0, 10);
  };
  const scheduleDelayMins = (s) => {
    const est = s?.estimated_time_utc ? new Date(s.estimated_time_utc).getTime() : NaN;
    const sch = (s?.scheduled_anchor_utc || s?.scheduled_time_utc)
      ? new Date(s.scheduled_anchor_utc || s.scheduled_time_utc).getTime()
      : NaN;
    if (!Number.isFinite(est) || !Number.isFinite(sch)) return null;
    return Math.round((est - sch) / 60000);
  };
  const isFutureSchedule = (iso) => {
    if (!iso) return false;
    const t = new Date(iso);
    return !Number.isNaN(t.getTime()) && t.getTime() > Date.now() + 60000;
  };
  const dedupeSchedules = (items = [], directionLabel = "Departure") => {
    const uniq = new Map();
    (Array.isArray(items) ? items : []).forEach((s) => {
      const anchor = scheduleAnchorTime(s);
      if (!anchor || !isFutureSchedule(anchor)) return;
      const dateKey = scheduleDateKey(s) || String(anchor).slice(0, 10);
      const key = `${directionLabel}|${dateKey}|${anchor}`;
      if (!uniq.has(key)) uniq.set(key, s);
    });
    return Array.from(uniq.values()).sort(
      (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
    );
  };

  const mapHeathrowSchedulesToBuckets = (payload) => {
    const rows = Array.isArray(payload?.schedules) ? payload.schedules : [];
    return {
      Arrival: rows.filter((r) => String(r?.direction || "").toLowerCase() === "arrival"),
      Departure: rows.filter((r) => String(r?.direction || "").toLowerCase() === "departure"),
    };
  };

  const availableFlightDirections = useMemo(() => {
    if (travelDirection === TRAVEL_DIRECTIONS.RETURN) return ["Arrival"];
    const next = [];
    if ((flightSchedules.Departure || []).length) next.push("Departure");
    if ((flightSchedules.Arrival || []).length) next.push("Arrival");
    return next;
  }, [travelDirection, flightSchedules]);

  const resolvedFlightDirection =
    travelDirection === TRAVEL_DIRECTIONS.RETURN
      ? "Arrival"
      : availableFlightDirections.includes(flightDirection)
      ? flightDirection
      : availableFlightDirections[0] || "Departure";

  const shouldShowFlightDirection = travelDirection !== TRAVEL_DIRECTIONS.RETURN && availableFlightDirections.length > 1;

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };
  const isLikelyFlightNumber = (value) => {
    const v = String(value || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!v) return false;
    return /^[A-Z0-9]{2,3}\d{1,4}$/.test(v);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((timer) => clearTimeout(timer));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const markLoadingStep = (id) => {
    setLoadingSteps((prev) => prev.map((step) => (step.id === id ? { ...step, status: "done" } : step)));
  };

  const startLoadingIndicators = ({ phase = "departure" } = {}) => {
    resetLoadingIndicators();
    const steps =
      phase === "arrival"
        ? [
            { id: "flight", label: "Checking live flight timing into Heathrow", status: "done" },
            { id: "road", label: "Checking road journey and Heathrow corridor timings", status: "pending" },
            { id: "rail", label: "Checking rail gateways, interchange timings and live station status", status: "pending" },
            { id: "weather", label: "Checking weather at home and Heathrow", status: "pending" },
            { id: "compare", label: "Building your return plan and comparing the best options home", status: "pending" },
          ]
        : [
            { id: "flight", label: "Checking live flight timing and departure window", status: "done" },
            { id: "road", label: "Checking live road traffic, motorway incidents and terminal timing", status: "pending" },
            { id: "rail", label: "Checking National Rail, RailData, live boards and gateway stations", status: "pending" },
            { id: "weather", label: "Checking weather at your postcode and at Heathrow", status: "pending" },
            { id: "compare", label: "Building your personalised journey plan and comparing modes", status: "pending" },
          ];

    setLoadingSteps(steps);
    loadingTimers.current.push(setTimeout(() => markLoadingStep("road"), 700));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("rail"), 1700));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("weather"), 3000));
    loadingTimers.current.push(setTimeout(() => markLoadingStep("compare"), 4700));
    loadingTimers.current.push(setTimeout(() => setLoadingSlow(true), 6500));
  };
  const selectPickupSuggestion = (postcode) => {
    setForm((prev) => ({ ...prev, pickup_postcode: postcode }));
    setPickupSuggestions([]);
    setPickupFocused(false);
    setPickupActiveIdx(-1);
    setPickupWarning("");
    setPostcodeError("");
  };
  const selectFlightSuggestion = (flightValue) => {
    const val = String(flightValue || "").toUpperCase();
    setForm((prev) => ({ ...prev, flight_number: val, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
    setFlightFocused(false);
    setFlightSuggestions([]);
    setFlightActiveIdx(-1);
    refreshFlightSchedules(val, { debounce: false, resetDate: true });
  };
  const handlePickupKeyDown = (e) => {
    const max = pickupSuggestions.length - 1;
    if (!pickupFocused || max < 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setPickupActiveIdx((prev) => (prev < max ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setPickupActiveIdx((prev) => (prev > 0 ? prev - 1 : max));
      return;
    }
    if (e.key === "Enter" && pickupActiveIdx >= 0 && pickupActiveIdx <= max) {
      e.preventDefault();
      const candidate = pickupSuggestions[pickupActiveIdx]?.postcode;
      if (candidate) selectPickupSuggestion(candidate);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setPickupFocused(false);
      setPickupActiveIdx(-1);
    }
  };
  const handleFlightKeyDown = (e) => {
    const max = flightSuggestions.length - 1;
    if (!flightFocused || max < 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFlightActiveIdx((prev) => (prev < max ? prev + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFlightActiveIdx((prev) => (prev > 0 ? prev - 1 : max));
      return;
    }
    if (e.key === "Enter" && flightActiveIdx >= 0 && flightActiveIdx <= max) {
      e.preventDefault();
      const candidate = flightSuggestions[flightActiveIdx]?.flightNumber;
      if (candidate) selectFlightSuggestion(candidate);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setFlightFocused(false);
      setFlightActiveIdx(-1);
    }
  };
  const pickupInputDescribedBy = [pickupWarning ? pickupWarningId : null, postcodeError ? postcodeErrorId : null]
    .filter(Boolean)
    .join(" ");
  const flightInputDescribedBy = [flightError ? flightErrorId : null].filter(Boolean).join(" ");

  const validatePostcodeExists = async (value, { silent = false } = {}) => {
    const pc = String(value || "").trim();
    if (!pc || !isLikelyUkPostcode(pc)) {
      if (!silent) setPostcodeError("Use a valid UK postcode (e.g. NG1 5FS).");
      return false;
    }
    try {
      setPostcodeValidating(true);
      const resp = await api.get("/api/postcodes/validate", { params: { postcode: pc } });
      const valid = !!resp.data?.valid;
      if (!valid) {
        if (!silent) setPostcodeError(resp.data?.error || "Postcode not found.");
        return false;
      }
      setPostcodeError("");
      return true;
    } catch (e) {
      if (!silent) setPostcodeError(e?.response?.data?.error || "Could not validate postcode.");
      return false;
    } finally {
      setPostcodeValidating(false);
    }
  };

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupActiveIdx(-1);
      setPickupWarning("");
      setPostcodeError("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", { params: { q: v, limit: 10 } });
        const suggestions = resp.data?.suggestions || [];
        const seen = new Map();
        suggestions.forEach((pc) => {
          const raw = String(pc || "").toUpperCase();
          const norm = raw.replace(/[^A-Z0-9]/g, "");
          if (!norm) return;
          if (!seen.has(norm)) {
            const formatted =
              norm.length > 3 ? `${norm.slice(0, norm.length - 3)} ${norm.slice(-3)}` : norm;
            seen.set(norm, formatted);
          }
        });
        setPickupSuggestions([...seen.values()].map((pc) => ({ postcode: pc, label: "Suggested" })));
        setPickupActiveIdx(-1);
      } catch (e) {
        setPickupSuggestions([]);
        setPickupActiveIdx(-1);
      }
    }, 180);
  };

  const fetchFlightSuggestions = (value) => {
    const v = String(value || "");
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      setFlightActiveIdx(-1);
      setFlightError(v.length === 0 ? "" : "Flight number format looks invalid.");
      return;
    }
    if (!isLikelyFlightNumber(v)) {
      setFlightError("Use flight format like FR9084 or TOM7260.");
    } else {
      setFlightError("");
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const params = {
          q: v,
          limit: 8,
          ...(travelDirection === TRAVEL_DIRECTIONS.RETURN ? { direction: "Arrival" } : {}),
        };
        const resp = await api.get(LHR_API.flightSuggest, { params });
        const list = resp.data?.suggestions || [];
        setFlightSuggestions(list.map((f) => ({ flightNumber: f })));
        setFlightActiveIdx(-1);
      } catch (e) {
        setFlightSuggestions([]);
        setFlightActiveIdx(-1);
      }
    }, 180);
  };

  useEffect(() => {
    if (!pickupFocused || pickupSuggestions.length === 0) {
      if (pickupActiveIdx !== -1) setPickupActiveIdx(-1);
      return;
    }
    if (pickupActiveIdx >= pickupSuggestions.length) {
      setPickupActiveIdx(pickupSuggestions.length - 1);
    }
  }, [pickupFocused, pickupSuggestions, pickupActiveIdx]);

  useEffect(() => {
    if (!flightFocused || flightSuggestions.length === 0) {
      if (flightActiveIdx !== -1) setFlightActiveIdx(-1);
      return;
    }
    if (flightActiveIdx >= flightSuggestions.length) {
      setFlightActiveIdx(flightSuggestions.length - 1);
    }
  }, [flightFocused, flightSuggestions, flightActiveIdx]);

  useEffect(() => {
    if (flightDirection === resolvedFlightDirection) return;
    setFlightDirection(resolvedFlightDirection);
    setForm((prev) => {
      if (prev.flight_direction === resolvedFlightDirection) return prev;
      return { ...prev, flight_direction: resolvedFlightDirection };
    });
  }, [flightDirection, resolvedFlightDirection]);

  useEffect(() => {
    refreshFlightSchedules(form.flight_number, { debounce: false, resetDate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!queryToken) return;
    let cancelled = false;
    const hydrateFromToken = async () => {
      setLoading(true);
      startLoadingIndicators({ phase: "token" });
      setError(null);
      try {
        let loaded = false;
        try {
          const tokenResp = await api.get(LHR_API.journeyByToken(queryToken));
          if (cancelled) return;
          const tokenData = tokenResp.data?.data || {};
          const isReturn = String(tokenData?.direction || "").toLowerCase() === "arrival";
          const planResp = isReturn
            ? await api.post(LHR_API.returnPlan, { token: queryToken })
            : await api.post(LHR_API.outwardPlan, { token: queryToken });
          if (cancelled) return;
          const payload = isReturn
            ? applyUiProjectionPreferred(
                normalizeReturnPlanToView(planResp.data?.data || null, {
                  pickup_postcode: tokenData?.postcode || form.pickup_postcode,
                  flight_number: tokenData?.flight_number || form.flight_number,
                  flight_date: tokenData?.flight_date || form.flight_date,
                }),
                planResp.data?.data?.ui_projection || planResp.data?.ui_projection || tokenData?.ui_projection || null
              )
            : applyUiProjectionPreferred(
                planResp.data?.data || null,
                planResp.data?.data?.ui_projection || planResp.data?.ui_projection || tokenData?.ui_projection || null
              );
          const prefill = payload?.input_prefill || {};
          if (payload) setResult(payload);
          setForm((prev) => ({
            ...prev,
            pickup_postcode: prefill.pickup_postcode || prev.pickup_postcode,
            flight_number: prefill.flight_number || prev.flight_number,
            flight_date: prefill.flight_date || prev.flight_date,
            flight_direction: prefill.flight_direction || prev.flight_direction,
            flight_anchor_utc: prefill.flight_anchor_utc || prev.flight_anchor_utc,
            passengers: Number(prefill.passengers || prev.passengers || 2),
            bags: Number(prefill.bags || prev.bags || 2),
            preferred_modes: Array.isArray(prefill.preferred_modes) ? prefill.preferred_modes : prev.preferred_modes,
            parking_duration_days: Number(prefill.parking_duration_days || prev.parking_duration_days || 4),
            buffer_mode: prefill.buffer_mode || prev.buffer_mode || "standard",
          }));
          if (prefill.travel_direction === TRAVEL_DIRECTIONS.RETURN) setTravelDirection(TRAVEL_DIRECTIONS.RETURN);
          setPostcodeError("");
          setFlightError("");
          if (prefill.flight_direction) setFlightDirection(prefill.flight_direction);
          if (prefill.flight_number) refreshFlightSchedules(prefill.flight_number, { debounce: false, resetDate: false });
          const j = tokenData || {};
          setJourneyMeta((prev) => ({
            ...prev,
            journey_id: j.journey_id || prev.journey_id,
            token: j.token || queryToken || prev.token,
            token_expires_at: j.token_expires_at || prev.token_expires_at,
            email: j.email || prev.email || "",
            email_verified: j.email_verified != null ? !!j.email_verified : prev.email_verified,
            note: isReturn ? "Loaded saved Heathrow return journey." : "Loaded saved Heathrow journey.",
          }));
          loaded = true;
        } catch {
          const rResp = await api.post(LHR_API.returnPlan, { token: queryToken });
          if (cancelled) return;
          const rJourney = rResp.data?.journey || {};
          const rData = rResp.data?.data || null;
          setTravelDirection(TRAVEL_DIRECTIONS.RETURN);
          if (rData) {
            const nextForm = {
              ...form,
              pickup_postcode: rJourney?.postcode || form.pickup_postcode,
              flight_number: rJourney?.flight_number || form.flight_number,
              flight_date: rJourney?.flight_date || form.flight_date,
              flight_direction: "Arrival",
              flight_anchor_utc: rData?.flight?.arrival || rData?.flight?.scheduled_arrival || form.flight_anchor_utc || "",
            };
            setForm((prev) => ({ ...prev, ...nextForm }));
            const normalized = normalizeReturnPlanToView(rData, nextForm);
            setResult(applyUiProjectionPreferred(normalized, rResp.data?.data?.ui_projection || rResp.data?.ui_projection || null));
            if (rJourney?.flight_number) refreshFlightSchedules(rJourney.flight_number, { debounce: false, resetDate: false });
          }
          setJourneyMeta((prev) => ({
            ...prev,
            journey_id: rJourney.journey_id || prev.journey_id,
            token: rJourney.token || queryToken || prev.token,
            token_expires_at: rJourney.token_expires_at || prev.token_expires_at,
            email: rJourney.email || prev.email || "",
            email_verified: rJourney.email_verified != null ? !!rJourney.email_verified : prev.email_verified,
            note: rResp.data?.cached ? "Loaded saved Heathrow return journey from cache." : "Loaded saved Heathrow return journey.",
          }));
          loaded = true;
        }
        if (!loaded && !cancelled) setError("Could not load journey from token.");
        setIsTokenBoundJourney(true);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || "Could not load journey from token.");
      } finally {
        if (!cancelled) {
          resetLoadingIndicators();
          setLoading(false);
        }
      }
    };
    hydrateFromToken();
    return () => {
      cancelled = true;
      resetLoadingIndicators();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryToken]);

  useEffect(
    () => () => {
      if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
      resetLoadingIndicators();
    },
    []
  );

  const refreshFlightSchedules = (flightNum, { debounce = true, resetDate = true, forceDirection = null } = {}) => {
    const clean = flightNumKey(flightNum);
    const requestedDirection = travelDirection === TRAVEL_DIRECTIONS.RETURN ? "Arrival" : forceDirection || resolvedFlightDirection;
    if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    if (!clean || clean.length < 2) {
      setFlightSchedules({ Arrival: [], Departure: [] });
      setFlightDirection(requestedDirection);
      setFlightDates([]);
      setFlightDateError("");
      setFlightError(clean.length ? "Use flight format like FR9084 or TOM7260." : "");
      if (resetDate) {
        setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
      }
      return;
    }

    const runner = async () => {
      const requestId = ++flightScheduleRequestSeq.current;
      const scheduleDirection = travelDirection === TRAVEL_DIRECTIONS.RETURN ? "Arrival" : "Departure";
      const shouldFetchArrival = travelDirection === TRAVEL_DIRECTIONS.RETURN;
      const shouldFetchDeparture = travelDirection !== TRAVEL_DIRECTIONS.RETURN;
      try {
        const lookupResp = await api
          .get(LHR_API.flightSchedules, { params: { flight_number: clean, direction: scheduleDirection } })
          .catch(() => null);
        if (requestId !== flightScheduleRequestSeq.current) return;
        const mapped = mapHeathrowSchedulesToBuckets(lookupResp?.data || null);
        const arrival = shouldFetchArrival ? dedupeSchedules(mapped.Arrival || [], "Arrival") : [];
        const departure = shouldFetchDeparture ? dedupeSchedules(mapped.Departure || [], "Departure") : [];
        const options =
          travelDirection === TRAVEL_DIRECTIONS.RETURN
            ? ["Arrival"]
            : [
                ...(departure.length ? ["Departure"] : []),
                ...(arrival.length ? ["Arrival"] : []),
              ];
        if (!options.length) options.push("Departure");
        const nextDirection = options.includes(requestedDirection) ? requestedDirection : options[0];

        setFlightDirection(nextDirection);
        setFlightSchedules({ Arrival: arrival, Departure: departure });
        if (!isLikelyFlightNumber(clean)) {
          setFlightError("Use flight format like FR9084 or TOM7260.");
        } else if (travelDirection === TRAVEL_DIRECTIONS.RETURN && !arrival.length) {
          setFlightError("Flight not found as a Heathrow arrival in our schedules.");
        } else if (travelDirection !== TRAVEL_DIRECTIONS.RETURN && !departure.length) {
          setFlightError("No upcoming Heathrow departures found for this flight.");
        } else {
          setFlightError("");
        }
        const selectedList = nextDirection === "Arrival" ? arrival : departure;
        if (travelDirection === TRAVEL_DIRECTIONS.RETURN && !arrival.length) {
          setFlightDateError("No upcoming Heathrow arrivals found for this flight.");
        } else if (!selectedList.length) {
          setFlightDateError(`No upcoming Heathrow ${nextDirection.toLowerCase()} slots found for this flight.`);
        } else {
          setFlightDateError("");
        }
        if (resetDate) {
          setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
        }
      } catch (e) {
        if (requestId !== flightScheduleRequestSeq.current) return;
        setFlightSchedules({ Arrival: [], Departure: [] });
        setFlightDirection(requestedDirection);
        setFlightDates([]);
        setFlightDateError("Select a valid flight date.");
        setFlightError("Could not check this flight right now.");
        if (resetDate) {
          setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
        }
      }
    };

    if (debounce) {
      flightScheduleTimer.current = setTimeout(runner, 180);
    } else {
      runner();
    }
  };

  useEffect(() => {
    if (queryToken) return;
    const queryPostcode = String(searchParams.get("pickup_postcode") || searchParams.get("postcode") || "").trim().toUpperCase();
    const queryFlight = flightNumKey(searchParams.get("flight_number") || searchParams.get("flight") || "");
    if (!queryPostcode && !queryFlight) return;

    setForm((prev) => ({
      ...prev,
      pickup_postcode: queryPostcode || prev.pickup_postcode,
      flight_number: queryFlight || prev.flight_number,
      flight_date: queryFlight ? "" : prev.flight_date,
      flight_direction: queryFlight ? "" : prev.flight_direction,
      flight_anchor_utc: queryFlight ? "" : prev.flight_anchor_utc,
    }));
    if (queryPostcode) {
      setPickupWarning("");
      setPostcodeError("");
    }
    if (queryFlight) {
      setFlightError("");
      refreshFlightSchedules(queryFlight, { debounce: false, resetDate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryToken, searchParams]);

  useEffect(() => {
    const effectiveDirection = resolvedFlightDirection;
    const merged = [
      ...(flightSchedules.Arrival || []).map((s) => ({ ...s, direction: "Arrival" })),
      ...(flightSchedules.Departure || []).map((s) => ({ ...s, direction: "Departure" })),
    ].filter((s) => s.direction === effectiveDirection);
    const unique = new Map();
    merged.forEach((s) => {
      const anchorIso = scheduleAnchorTime(s);
      if (!anchorIso || !isFutureSchedule(anchorIso)) return;
      const dayKey = scheduleDateKey(s) || String(anchorIso).slice(0, 10);
      const key = `${s.direction}|${dayKey}|${anchorIso}`;
      if (!unique.has(key)) unique.set(key, s);
    });
    const nextDates = Array.from(unique.values());
    nextDates.sort((a, b) => {
      const ta = new Date(scheduleAnchorTime(a) || a.date || a.snapshot_date).getTime();
      const tb = new Date(scheduleAnchorTime(b) || b.date || b.snapshot_date).getTime();
      return ta - tb;
    });
    setFlightDates(nextDates);

    if (form.flight_date) {
      const hasSelection = nextDates.some((s) => {
        const optionDate = scheduleDateKey(s);
        const optionAnchor = String(scheduleAnchorTime(s) || "");
        if (String(optionDate) !== String(form.flight_date || "")) return false;
        if (!form.flight_anchor_utc) return true;
        return optionAnchor === String(form.flight_anchor_utc || "");
      });
      if (!hasSelection) {
        setForm((prev) => ({ ...prev, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
      }
    }
    if (!nextDates.length && form.flight_number) {
      setFlightDateError("Select a valid flight date.");
    } else {
      setFlightDateError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightSchedules, resolvedFlightDirection, form.flight_date, form.flight_anchor_utc, form.flight_number]);

  useEffect(() => {
    const targetDefault = travelDirection === TRAVEL_DIRECTIONS.RETURN ? DEFAULT_FLIGHT_NUMBER_FROM_LHR : DEFAULT_FLIGHT_NUMBER_TO_LHR;
    const nextFlightNumber =
      !form.flight_number ||
      form.flight_number === DEFAULT_FLIGHT_NUMBER_TO_LHR ||
      form.flight_number === DEFAULT_FLIGHT_NUMBER_FROM_LHR
        ? targetDefault
        : form.flight_number;
    setForm((prev) => ({
      ...prev,
      flight_number: nextFlightNumber,
      flight_date: "",
      flight_direction: travelDirection === TRAVEL_DIRECTIONS.RETURN ? "Arrival" : "",
      flight_anchor_utc: "",
    }));
    setFlightDirection(travelDirection === TRAVEL_DIRECTIONS.RETURN ? "Arrival" : "Departure");
    refreshFlightSchedules(nextFlightNumber, { debounce: false, resetDate: true });
    if (isTokenBoundJourney) setIsTokenBoundJourney(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelDirection]);

  useEffect(() => {
    if (!result || !scrollToResultsPending) return;
    setScrollToResultsPending(false);
    const target = resultContainerRef.current;
    if (!target) return;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [result, scrollToResultsPending]);

  const setStepView = (stepValue) => {
    const next = new URLSearchParams(searchParams);
    if (stepValue) next.set("step", stepValue);
    else next.delete("step");
    setSearchParams(next, { replace: true });
  };

  const submit = async () => {
    if (USE_MOCK && MOCK_RESPONSE) {
      setScrollToResultsPending(true);
      setResult(MOCK_RESPONSE);
      if (isCompactMobile) setStepView("results");
      setLoading(false);
      setError(null);
      return;
    }

    if (pickupWarning || !isLikelyUkPostcode(form.pickup_postcode)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
      setPostcodeError("Use a valid UK postcode (e.g. NG1 5FS).");
      setError("Please enter a valid pickup postcode.");
      return;
    }
    const postcodeOk = await validatePostcodeExists(form.pickup_postcode, { silent: false });
    if (!postcodeOk) {
      setError("Please enter a valid postcode that exists.");
      return;
    }
    if (!form.flight_number) {
      setError("Enter a flight number.");
      return;
    }
    if (!isLikelyFlightNumber(form.flight_number)) {
      setFlightError("Use flight format like FR9084 or TOM7260.");
      setError("Please enter a valid flight number.");
      return;
    }
    if (flightError) {
      setError(flightError);
      return;
    }
    if (travelDirection === TRAVEL_DIRECTIONS.RETURN && !(flightSchedules.Arrival || []).length) {
      setFlightError("Flight not found as a Heathrow arrival in our schedules.");
      setError("This flight is not available for Heathrow arrivals.");
      return;
    }
    if (travelDirection !== TRAVEL_DIRECTIONS.RETURN && !(flightSchedules.Arrival || []).length && !(flightSchedules.Departure || []).length) {
      setFlightError(
        travelDirection === TRAVEL_DIRECTIONS.RETURN
          ? "No upcoming Heathrow arrivals found for this flight."
          : "No upcoming Heathrow departures found for this flight."
      );
      setError("This flight is not available in Heathrow schedules.");
      return;
    }
    if (!form.flight_date) {
      setFlightDateError("Pick a flight date.");
      setError("Pick a flight date.");
      return;
    }
    if (!form.flight_anchor_utc) {
      setError("Flight time missing; re-select the flight date/time.");
      return;
    }

    setLoading(true);
    startLoadingIndicators({ phase: travelDirection === TRAVEL_DIRECTIONS.RETURN ? "arrival" : "departure" });
    setScrollToResultsPending(true);
    setError(null);
    setSubErrors([]);
    setResult(null);

    try {
      const cleanPostcode = form.pickup_postcode.replace(/\s+/g, "");
      if (travelDirection === TRAVEL_DIRECTIONS.RETURN) {
        setJourneyMeta((prev) => ({ ...prev, creating: true, note: "" }));
        const returnResp = await api.post(LHR_API.returnPlan, {
          token: isTokenBoundJourney ? journeyMeta.token || undefined : undefined,
          postcode: cleanPostcode,
          flight_number: form.flight_number,
          flight_date: form.flight_date,
          flight_anchor_utc: form.flight_anchor_utc,
          direction: "Arrival",
          passengers: Number(form.passengers || 1),
          bags: Number(form.bags || 0),
          include_google: true,
          terminal_exit_buffer_mins: 40,
        });
        const normalized = normalizeReturnPlanToView(returnResp.data?.data || null, form);
        setResult(applyUiProjectionPreferred(normalized, returnResp.data?.data?.ui_projection || returnResp.data?.ui_projection || null));
        if (isCompactMobile) setStepView("results");
        const jData = returnResp.data?.journey || {};
        setJourneyMeta((prev) => ({
          ...prev,
          creating: false,
          journey_id: jData.journey_id || prev.journey_id,
          token: jData.token || prev.token,
          token_expires_at: jData.token_expires_at || prev.token_expires_at,
          email: jData.email || prev.email || "",
          email_verified: jData.email_verified != null ? !!jData.email_verified : prev.email_verified,
          note: returnResp.data?.cached ? "Loaded cached return journey." : "Return journey evaluated (LHR -> Home).",
        }));
      } else {
        setJourneyMeta((prev) => ({ ...prev, creating: true, note: "" }));
        const evalResp = await api.post(LHR_API.outwardPlan, {
          token: isTokenBoundJourney ? journeyMeta.token || undefined : undefined,
          postcode: cleanPostcode,
          flight_number: form.flight_number,
          flight_date: form.flight_date,
          direction: form.flight_direction || resolvedFlightDirection,
          passengers: Number(form.passengers || 1),
          bags: Number(form.bags || 0),
          preferred_modes: form.preferred_modes,
          include_google: true,
        });

        const nextResult = applyUiProjectionPreferred(evalResp.data?.data || null, evalResp.data?.data?.ui_projection || evalResp.data?.ui_projection || null);
        setResult(nextResult);
        if (isCompactMobile) setStepView("results");
        const jData = evalResp.data?.journey || {};
        setJourneyMeta((prev) => ({
          ...prev,
          creating: false,
          journey_id: jData.journey_id || prev.journey_id,
          token: jData.token || prev.token,
          token_expires_at: jData.token_expires_at || prev.token_expires_at,
          email: jData.email || prev.email || "",
          email_verified: jData.email_verified != null ? !!jData.email_verified : prev.email_verified,
          note: evalResp.data?.cached ? "Loaded cached evaluation." : "Journey evaluated and saved.",
        }));
      }
    } catch (err) {
      setScrollToResultsPending(false);
      setJourneyMeta((prev) => ({ ...prev, creating: false }));
      setError(err.response?.data?.error || err.message || "Something went wrong");
    } finally {
      resetLoadingIndicators();
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    const token = String(journeyMeta?.token || "").trim();
    const email = String(journeyMeta?.email || "").trim();
    if (!token) {
      setJourneyMeta((prev) => ({ ...prev, note: "Save or load a journey first." }));
      return;
    }
    if (!email) {
      setJourneyMeta((prev) => ({ ...prev, note: "Enter your email first." }));
      return;
    }
    try {
      setJourneyMeta((prev) => ({ ...prev, emailing: true, note: "" }));
      await api.post(LHR_API.journeySendEmail(token), { email });
      setJourneyMeta((prev) => ({
        ...prev,
        emailing: false,
        email_sent: true,
        email_verified: false,
        note: "Verification code sent.",
      }));
    } catch (e) {
      setJourneyMeta((prev) => ({
        ...prev,
        emailing: false,
        note: e?.response?.data?.error || e?.message || "Could not send verification code.",
      }));
    }
  };

  const verifyJourneyCode = async () => {
    const token = String(journeyMeta?.token || "").trim();
    const code = String(verificationCode || "").trim();
    if (!token) {
      setJourneyMeta((prev) => ({ ...prev, note: "Save or load a journey first." }));
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setJourneyMeta((prev) => ({ ...prev, note: "Enter a valid 6-digit code." }));
      return;
    }
    try {
      setJourneyMeta((prev) => ({ ...prev, verifying: true, note: "" }));
      await api.post(LHR_API.journeyVerifyCode(token), { code });
      setVerificationCode("");
      setJourneyMeta((prev) => ({
        ...prev,
        verifying: false,
        email_verified: true,
        email_sent: false,
        note: "Email verified. Live monitoring is active.",
      }));
    } catch (e) {
      setJourneyMeta((prev) => ({
        ...prev,
        verifying: false,
        note: e?.response?.data?.error || e?.message || "Could not verify code.",
      }));
    }
  };

  const renderLoading = () => {
    if (!loading || !loadingSteps.length) return null;
    return (
      <div
        style={{
          marginTop: 14,
          border: "1px solid #dbe4f0",
          borderRadius: 16,
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
            color: "#fff",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800 }}>Building your personalised Heathrow journey</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
            We are checking live flight, road, rail, weather and airport timing data.
          </div>
        </div>
        <div style={{ padding: 14, display: "grid", gap: 9 }}>
          {loadingSteps.map((step) => (
            <div
              key={step.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "#0f172a",
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: step.status === "done" ? "#dcfce7" : "#e0e7ff",
                  color: step.status === "done" ? "#166534" : "#1d4ed8",
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {step.status === "done" ? "✓" : "…"}
              </span>
              <span>{step.label}</span>
            </div>
          ))}
          {loadingSlow && (
            <div
              style={{
                marginTop: 2,
                borderRadius: 12,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                color: "#9a3412",
                padding: "10px 12px",
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              Live data calls can take a few extra seconds when rail, road and airport sources are all being checked together. Your journey is still processing.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>How to Get to Heathrow | Live Journey Planner (2026)</title>

        <meta
          name="description"
          content="Plan when to leave for Heathrow using live road traffic, rail timing, parking transfer data, weather and flight status."
        />

        <link
          rel="canonical"
          href={canonicalUrl}
        />

        <meta property="og:title" content="How to Get to Heathrow | Live Journey Planner (2026)" />
        <meta
          property="og:description"
          content="Plan when to leave for Heathrow using live road traffic, rail timing, parking transfer data, weather and flight status."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${canonicalUrl}#webpage`,
            name: "How to Get to Heathrow | Live Journey Planner (2026)",
            url: canonicalUrl,
            description:
              "Plan when to leave for Heathrow using live road traffic, rail timing, parking transfer data, weather and flight status.",
            inLanguage: "en-GB",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How does this journey planner calculate when to leave?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The planner evaluates live road traffic, rail services, transfer times and flight departure windows to calculate a recommended departure time from your home.",
                },
              },
              {
                "@type": "Question",
                name: "Does this monitor disruptions before my flight?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Verified journeys can be monitored in the hours before departure using road, rail and flight status data to detect significant changes.",
                },
              },
              {
                "@type": "Question",
                name: "Does Heathrow have rail access?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Heathrow has direct rail access, and the planner evaluates the best gateway and terminal walk for your route.",
                },
              },
              {
                "@type": "Question",
                name: "Which rail route is best for Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The best route depends on your origin, interchange timing and terminal. The planner compares Heathrow rail gateways and terminal transfers before recommending a route.",
                },
              },
              {
                "@type": "Question",
                name: "Is this an official Heathrow system?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. This is an independent travel planning tool that uses official transport data feeds to assist passengers travelling to Heathrow.",
                },
              },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["SoftwareApplication", "WebApplication"],
            name: "Heathrow Live Journey Planner",
            url: canonicalUrl,
            operatingSystem: "All",
            applicationCategory: "TravelApplication",
            applicationSubCategory: "Journey planning",
            description:
              "A live journey planning and monitoring tool for Heathrow, combining road traffic, rail services, weather and flight status to calculate recommended departure times.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            featureList: [
              "Flight-aware leave time calculation",
              "Rail, taxi and self-drive comparisons",
              "Real-time road and rail disruption updates",
              "Heathrow terminal transfer integration",
              "Luggage and walking impact assessment",
            ],
          })}
        </script>
      </Helmet>

      {!showMobileResultsScreen && <LiveStrip />}

      {!showMobileResultsScreen && (
      <>
      <div className="parking-header">
        <h1>How to Get to Heathrow (Live Journey Planner &amp; Leave-Time Calculator 2026)</h1>
        <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0f172a" }}>
          Updated: 12th Apr 2026 • Door-to-terminal live journey monitor
        </div>
        <div style={{ fontSize: 11, color: "#475467", marginTop: 6 }}>
          Live road, rail and flight monitoring with structured disruption detection — calculating when to leave and adjusting your arrival window as conditions evolve.
        </div>
      </div>

      <div className="parking-form ema-v2-form" style={{ marginTop: 18 }}>
        <div className="ema-v2-form-grid" style={{ display: "grid", gap: 12 }}>
          <div
            className="ema-v2-route-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: isCompactMobile ? "column" : "row",
              gap: isCompactMobile ? 12 : 10,
              alignItems: isCompactMobile ? "stretch" : "end",
              justifyContent: "space-between",
            }}
          >
            {travelDirection === TRAVEL_DIRECTIONS.TOWARDS ? (
              <>
                <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320 }}>
                  Postcode
                  <input
                    className="parking-input"
                    value={form.pickup_postcode}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, pickup_postcode: e.target.value }));
                      if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                      setPostcodeError("");
                      fetchPickupSuggestions(e.target.value);
                    }}
                    onKeyDown={handlePickupKeyDown}
                    onFocus={() => setPickupFocused(true)}
                    onBlur={() =>
                      setTimeout(() => {
                        setPickupFocused(false);
                        setPickupActiveIdx(-1);
                        validatePostcodeExists(form.pickup_postcode, { silent: false });
                      }, 200)
                    }
                    placeholder="e.g. NG1 5FS"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={pickupFocused && pickupSuggestions.length > 0}
                    aria-controls={pickupListboxId}
                    aria-activedescendant={
                      pickupFocused && pickupActiveIdx >= 0
                        ? `ema-v2-pickup-option-${pickupActiveIdx}`
                        : undefined
                    }
                    aria-invalid={!!pickupWarning || !!postcodeError}
                    aria-describedby={pickupInputDescribedBy || undefined}
                  />
                  {pickupWarning && (
                    <div id={pickupWarningId} className="input-warning" role="status" aria-live="polite">
                      {pickupWarning}
                    </div>
                  )}
                  {postcodeError && (
                    <div id={postcodeErrorId} className="input-warning" role="status" aria-live="polite">
                      {postcodeError}
                    </div>
                  )}
                  {pickupFocused && pickupSuggestions.length > 0 && (
                    <div className="suggestions" id={pickupListboxId} role="listbox" aria-label="Postcode suggestions">
                      {pickupSuggestions.map((s, idx) => (
                        <button
                          type="button"
                          key={s.postcode}
                          className="suggestion"
                          style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                          id={`ema-v2-pickup-option-${idx}`}
                          role="option"
                          aria-selected={idx === pickupActiveIdx}
                          onMouseEnter={() => setPickupActiveIdx(idx)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectPickupSuggestion(s.postcode);
                          }}
                        >
                          {s.postcode} <span className="suggestion-label">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </label>
                <button
                  className="ema-v2-swap-btn"
                  type="button"
                  onClick={() => {
                    const valid = isLikelyUkPostcode(form.pickup_postcode);
                    if (!valid) {
                      setPickupWarning("Enter a valid UK postcode before swapping route direction.");
                      return;
                    }
                    setTravelDirection(TRAVEL_DIRECTIONS.RETURN);
                    setIsTokenBoundJourney(false);
                  }}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    border: "2px solid #4f46e5",
                    background: "#fff",
                    color: "#312e81",
                    fontSize: 20,
                    fontWeight: 800,
                    cursor: isLikelyUkPostcode(form.pickup_postcode) ? "pointer" : "not-allowed",
                    opacity: isLikelyUkPostcode(form.pickup_postcode) ? 1 : 0.45,
                    alignSelf: "center",
                  }}
                  title="Swap direction"
                >
                  ⇄
                </button>
                <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320 }}>
                  Heathrow Airport
                  <input className="parking-input" value="Heathrow Airport (locked)" disabled style={{ background: "#f8fafc", color: "#475467" }} />
                </label>
              </>
            ) : (
              <>
                <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320 }}>
                  Heathrow Airport
                  <input className="parking-input" value="Heathrow Airport (locked)" disabled style={{ background: "#f8fafc", color: "#475467" }} />
                </label>
                <button
                  className="ema-v2-swap-btn"
                  type="button"
                  onClick={() => {
                    const valid = isLikelyUkPostcode(form.pickup_postcode);
                    if (!valid) {
                      setPickupWarning("Enter a valid UK postcode before swapping route direction.");
                      return;
                    }
                    setTravelDirection(TRAVEL_DIRECTIONS.TOWARDS);
                    setIsTokenBoundJourney(false);
                  }}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    border: "2px solid #4f46e5",
                    background: "#fff",
                    color: "#312e81",
                    fontSize: 20,
                    fontWeight: 800,
                    cursor: isLikelyUkPostcode(form.pickup_postcode) ? "pointer" : "not-allowed",
                    opacity: isLikelyUkPostcode(form.pickup_postcode) ? 1 : 0.45,
                    alignSelf: "center",
                  }}
                  title="Swap direction"
                >
                  ⇄
                </button>
                <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 320 }}>
                  Postcode
                  <input
                    className="parking-input"
                    value={form.pickup_postcode}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, pickup_postcode: e.target.value }));
                      if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                      setPostcodeError("");
                      fetchPickupSuggestions(e.target.value);
                    }}
                    onKeyDown={handlePickupKeyDown}
                    onFocus={() => setPickupFocused(true)}
                    onBlur={() =>
                      setTimeout(() => {
                        setPickupFocused(false);
                        setPickupActiveIdx(-1);
                        validatePostcodeExists(form.pickup_postcode, { silent: false });
                      }, 200)
                    }
                    placeholder="e.g. NG1 5FS"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={pickupFocused && pickupSuggestions.length > 0}
                    aria-controls={pickupListboxId}
                    aria-activedescendant={
                      pickupFocused && pickupActiveIdx >= 0
                        ? `ema-v2-pickup-option-${pickupActiveIdx}`
                        : undefined
                    }
                    aria-invalid={!!pickupWarning || !!postcodeError}
                    aria-describedby={pickupInputDescribedBy || undefined}
                  />
                  {pickupWarning && (
                    <div id={pickupWarningId} className="input-warning" role="status" aria-live="polite">
                      {pickupWarning}
                    </div>
                  )}
                  {postcodeError && (
                    <div id={postcodeErrorId} className="input-warning" role="status" aria-live="polite">
                      {postcodeError}
                    </div>
                  )}
                  {pickupFocused && pickupSuggestions.length > 0 && (
                    <div className="suggestions" id={pickupListboxId} role="listbox" aria-label="Postcode suggestions">
                      {pickupSuggestions.map((s, idx) => (
                        <button
                          type="button"
                          key={s.postcode}
                          className="suggestion"
                          style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                          id={`ema-v2-pickup-option-${idx}`}
                          role="option"
                          aria-selected={idx === pickupActiveIdx}
                          onMouseEnter={() => setPickupActiveIdx(idx)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectPickupSuggestion(s.postcode);
                          }}
                        >
                          {s.postcode} <span className="suggestion-label">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </label>
              </>
            )}
          </div>

          <div
            className="ema-v2-flight-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              flexDirection: isCompactMobile ? "column" : "row",
              gap: isCompactMobile ? 12 : 10,
              alignItems: isCompactMobile ? "stretch" : "end",
              justifyContent: "space-between",
            }}
          >

          <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 170px", maxWidth: isCompactMobile ? "100%" : 260 }}>
            Flight number
            <input
              className="parking-input"
              value={form.flight_number}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                setForm((prev) => ({ ...prev, flight_number: val, flight_date: "", flight_direction: "", flight_anchor_utc: "" }));
                if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                if (val && !isLikelyFlightNumber(val)) setFlightError("Use flight format like FR9084 or TOM7260.");
                else setFlightError("");
                fetchFlightSuggestions(val);
                refreshFlightSchedules(val, { debounce: true, resetDate: true });
              }}
              onKeyDown={handleFlightKeyDown}
              onFocus={() => setFlightFocused(true)}
              onBlur={() =>
                setTimeout(() => {
                  setFlightFocused(false);
                  setFlightActiveIdx(-1);
                }, 200)
              }
              placeholder="e.g. FR123"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={flightFocused && flightSuggestions.length > 0}
              aria-controls={flightListboxId}
              aria-activedescendant={
                flightFocused && flightActiveIdx >= 0
                  ? `ema-v2-flight-option-${flightActiveIdx}`
                  : undefined
              }
              aria-invalid={!!flightError}
              aria-describedby={flightInputDescribedBy || undefined}
            />
            {flightError && (
              <div id={flightErrorId} className="input-warning" role="status" aria-live="polite">
                {flightError}
              </div>
            )}
            {flightFocused && flightSuggestions.length > 0 && (
              <div className="suggestions" id={flightListboxId} role="listbox" aria-label="Flight number suggestions">
                {flightSuggestions.map((s, idx) => (
                  <button
                    type="button"
                    key={s.id || s.flightNumber}
                    className="suggestion"
                    style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                    id={`ema-v2-flight-option-${idx}`}
                    role="option"
                    aria-selected={idx === flightActiveIdx}
                    onMouseEnter={() => setFlightActiveIdx(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectFlightSuggestion(s.flightNumber || s.id || s);
                    }}
                  >
                    {s.flightNumber || s.id || s}
                  </button>
                ))}
              </div>
            )}
          </label>

          {shouldShowFlightDirection && (
            <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 180px", maxWidth: isCompactMobile ? "100%" : 220 }}>
              Flight direction
              <select
                className="parking-input"
                value={resolvedFlightDirection}
                onChange={(e) => {
                  const nextDirection = String(e.target.value || "Departure") === "Arrival" ? "Arrival" : "Departure";
                  setFlightDirection(nextDirection);
                  setForm((prev) => ({
                    ...prev,
                    flight_direction: nextDirection,
                    flight_date: "",
                    flight_anchor_utc: "",
                  }));
                  if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                  refreshFlightSchedules(form.flight_number, {
                    debounce: false,
                    resetDate: true,
                    forceDirection: nextDirection,
                  });
                }}
              >
                {availableFlightDirections.includes("Departure") && <option value="Departure">Departs Heathrow</option>}
                {availableFlightDirections.includes("Arrival") && <option value="Arrival">Arrives at Heathrow</option>}
              </select>
            </label>
          )}

          <label className="parking-label ema-v2-field" style={{ flex: isCompactMobile ? "1 1 100%" : "1 1 240px", maxWidth: isCompactMobile ? "100%" : 360 }}>
            Flight date / time
            <select
              className="parking-input"
              value={
                form.flight_date
                  ? `${form.flight_direction || resolvedFlightDirection}|${form.flight_date}|${form.flight_anchor_utc || ""}`
                  : ""
              }
              aria-invalid={!!flightDateError}
              aria-describedby={flightDateError ? flightDateErrorId : undefined}
              onChange={(e) => {
                const [dir, val, anchor] = String(e.target.value || "").split("|");
                setForm((prev) => ({
                  ...prev,
                  flight_date: val || "",
                  flight_direction: dir || "",
                  flight_anchor_utc: anchor || "",
                }));
                if (isTokenBoundJourney) setIsTokenBoundJourney(false);
                if (dir) {
                  setFlightDirection(dir);
                }
                setFlightDateError("");
              }}
            >
              <option value="">Select flight date</option>
              {flightDates.map((s) => {
                const d = scheduleDateKey(s) || s.date || s.snapshot_date || s;
                const anchorIso = scheduleAnchorTime(s);
                const timeLabel = anchorIso ? formatScheduleLabel(anchorIso) : d || "—";
                const labelPrefix = s.direction === "Arrival" ? "Arrives at Heathrow — " : "Departs Heathrow — ";
                const terminalLabel = s.terminal ? ` • T${s.terminal}` : "";
                const delayMins = scheduleDelayMins(s);
                const delayLabel =
                  delayMins == null
                    ? ""
                    : delayMins > 0
                    ? ` • Delay +${delayMins}m`
                    : delayMins < 0
                    ? ` • Early ${Math.abs(delayMins)}m`
                    : "";
                const statusLabel = s.status ? ` • ${String(s.status)}` : "";
                const label = `${labelPrefix}${timeLabel}${terminalLabel}${delayLabel}${statusLabel}`;
                const val = String(d || "").slice(0, 10);
                return (
                  <option
                    key={`${flightNumKey(form.flight_number)}-${s.direction}-${val}-${anchorIso || ""}`}
                    value={`${s.direction}|${val}|${anchorIso || ""}`}
                  >
                    {label}
                  </option>
                );
              })}
              {!flightDates.length && form.flight_date && (
                <option value={`${resolvedFlightDirection}|${form.flight_date}|${form.flight_anchor_utc || ""}`}>
                  {form.flight_date}
                </option>
              )}
            </select>
            {flightDateError && (
              <div id={flightDateErrorId} className="input-warning" role="status" aria-live="polite">
                {flightDateError}
              </div>
            )}
          </label>

            <div className="parking-actions ema-v2-submit-wrap" style={{ alignSelf: isCompactMobile ? "stretch" : "end", marginTop: 0, marginLeft: isCompactMobile ? 0 : "auto", width: isCompactMobile ? "100%" : "auto" }}>
              <button
                className="parking-btn ema-v2-submit-btn"
                onClick={submit}
                disabled={loading || postcodeValidating || !!postcodeError || !!flightError || !!pickupWarning || !form.flight_date || !form.flight_anchor_utc}
                style={{ minWidth: isCompactMobile ? "100%" : 220, width: isCompactMobile ? "100%" : "auto", paddingLeft: 22, paddingRight: 22 }}
              >
                {loading ? "Building your journey..." : "Get my travel plan"}
              </button>
            {error && (
              <div id={formErrorId} className="input-warning" style={{ marginTop: 8 }} role="alert" aria-live="assertive">
                {error}
              </div>
            )}
          </div>
          </div>
        </div>

        <details
          open={advancedOpen}
          onToggle={(e) => setAdvancedOpen(e.target.open)}
          style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff" }}
        >
          <summary style={{ padding: "10px 12px", cursor: "pointer", fontWeight: 700, color: "#0f172a" }}>
            Adjust assumptions (optional)
          </summary>
          <div style={{ padding: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <label className="parking-label">
              Passengers
              <input
                className="parking-input"
                type="number"
                min="1"
                max="8"
                value={form.passengers}
                onChange={(e) => setForm((prev) => ({ ...prev, passengers: Number(e.target.value) }))}
              />
            </label>
            <label className="parking-label">
              Bags
              <input
                className="parking-input"
                type="number"
                min="0"
                max="12"
                value={form.bags}
                onChange={(e) => setForm((prev) => ({ ...prev, bags: Number(e.target.value) }))}
              />
            </label>
            <label className="parking-label">
              Preferred modes
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                {["taxi", "rail", "self_drive"].map((m) => (
                  <label key={m} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={form.preferred_modes.includes(m)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => {
                          const set = new Set(prev.preferred_modes || []);
                          if (checked) set.add(m);
                          else set.delete(m);
                          return { ...prev, preferred_modes: Array.from(set) };
                        });
                      }}
                    />
                    {m === "self_drive" ? "Self-drive" : m === "rail" ? "Rail" : "Taxi"}
                  </label>
                ))}
              </div>
            </label>
            {form.preferred_modes.includes("self_drive") && (
              <label className="parking-label">
                Parking stay (days)
                <input
                  className="parking-input"
                  type="number"
                  min="1"
                  max="30"
                  value={form.parking_duration_days}
                  onChange={(e) => setForm((prev) => ({ ...prev, parking_duration_days: Number(e.target.value) }))}
                />
              </label>
            )}
            <label className="parking-label">
              Buffer mode
              <select
                className="parking-input"
                value={form.buffer_mode}
                onChange={(e) => setForm((prev) => ({ ...prev, buffer_mode: e.target.value }))}
              >
                <option value="standard">Standard buffers</option>
                <option value="fasttrack">FastTrack / light buffer</option>
              </select>
              <div className="input-hint">Tuning only; still using standard buffers in this beta.</div>
            </label>
          </div>
        </details>
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            lineHeight: 1.35,
            color: "#6b7280",
          }}
        >
          Live journey monitoring uses official transport data feeds, including National Rail service information, National Highways incident data, Google traffic conditions, Open-Meteo weather forecasts and airline operational status updates.
        </div>
      </div>
      </>
      )}

      {USE_MOCK && result && <div style={{ marginTop: 20 }}>Mock response loaded.</div>}

      {renderLoading()}

      {subErrors.length > 0 && (
        <div className="input-warning" style={{ marginTop: 12 }}>
          {subErrors.join(" • ")}
        </div>
      )}

      {result && (!isCompactMobile || showMobileResultsScreen) && (
        <div
          ref={resultContainerRef}
          style={{
            marginTop: showMobileResultsScreen ? 8 : 20,
            display: "grid",
            gap: 12,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {showMobileResultsScreen && (
            <button
              type="button"
              onClick={() => setStepView("")}
              style={{
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
                borderRadius: 16,
                padding: "12px 14px",
                width: "100%",
                boxSizing: "border-box",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              ← Edit journey
            </button>
          )}
          {travelDirection === TRAVEL_DIRECTIONS.RETURN || result?.return_guidance ? (
            <HeathrowReturnRecommendationSummary result={result} viewForm={form} journeyMeta={journeyMeta} />
          ) : (
            <HeathrowDepartureRecommendationSummary result={result} flightForm={form} journeyMeta={journeyMeta} />
          )}
          <SecureJourneyAccessStrip
            journeyMeta={journeyMeta}
            setJourneyMeta={setJourneyMeta}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            onSendVerificationEmail={sendVerificationEmail}
            onVerifyCode={verifyJourneyCode}
            verificationAvailable={true}
          />
        </div>
      )}

      {!showMobileResultsScreen && (
        <>
          <HeathrowRelatedToolsSection />
          <CollapsibleSeoSections />
        </>
      )}
    </div>
  );
}
